import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Users, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { products } from '../stripe-config';
import { redirectToCheckout } from '../lib/stripe';
import { useDocumentMeta } from '../lib/useDocumentMeta';

type State =
  | { status: 'idle' }
  | { status: 'joined'; team_name: string; team_id: string }
  | { status: 'overflow'; team_name: string; team_id: string }
  | { status: 'error'; message: string };

export default function TeamJoinPage() {
  const { token } = useParams<{ token: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ status: 'idle' });
  const [working, setWorking] = useState(false);

  useDocumentMeta({ title: 'Join team — PromptGPT', description: 'Accept your team invite' });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`/team/join/${token}`)}`);
      return;
    }
    const accept = async () => {
      const { data, error } = await supabase.rpc('accept_team_invite', { p_token: token });
      if (error) {
        setState({ status: 'error', message: error.message });
        return;
      }
      const status = (data as { status: string })?.status;
      const teamName = (data as { team_name?: string })?.team_name || 'this team';
      const teamId = (data as { team_id?: string })?.team_id || '';
      if (status === 'joined' || status === 'already_member') {
        setState({ status: 'joined', team_name: teamName, team_id: teamId });
      } else if (status === 'overflow') {
        setState({ status: 'overflow', team_name: teamName, team_id: teamId });
      } else if (status === 'expired') {
        setState({ status: 'error', message: 'This invite link has expired. Ask the team owner for a new one.' });
      } else if (status === 'revoked') {
        setState({ status: 'error', message: 'This invite link has been revoked. Ask the team owner for a new one.' });
      } else {
        setState({ status: 'error', message: 'This invite link is not valid.' });
      }
    };
    accept();
  }, [token, user, authLoading, navigate]);

  const joinAtPro = async () => {
    if (state.status !== 'overflow') return;
    setWorking(true);
    try {
      // Mark redemption as overflow_pro and add as personal_pro member ahead of checkout
      await supabase.rpc('complete_overflow_pro', { p_team_id: state.team_id });
      await redirectToCheckout(products.pro.priceId, 'subscription', {
        success_url: `${window.location.origin}/team/workspace`,
        cancel_url: `${window.location.origin}/team/join/${token}`,
      });
    } catch (e) {
      toast.error((e as Error).message);
      setWorking(false);
    }
  };

  const requestSecondTeam = async () => {
    if (state.status !== 'overflow' || !user) return;
    setWorking(true);
    const { error } = await supabase.from('team_overflow_requests').insert({
      team_id: state.team_id,
      user_id: user.id,
    });
    if (error) {
      toast.error(error.message);
      setWorking(false);
      return;
    }
    toast.success('Request sent to the team owner');
    setState({ status: 'error', message: `Request sent. The owner of ${state.team_name} will see it and can add a second Team plan.` });
    setWorking(false);
  };

  return (
    <div className="min-h-screen grid place-items-center bg-warm-50 px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-soft border border-ink-100 p-8 md:p-10">
        <div className="w-12 h-12 rounded-full bg-accent-100 grid place-items-center mb-5">
          <Users className="w-6 h-6 text-accent-700" />
        </div>

        {state.status === 'idle' && (
          <>
            <h1 className="font-display text-2xl mb-1">Joining team...</h1>
            <p className="text-sm text-ink-600">Validating your invite link.</p>
          </>
        )}

        {state.status === 'joined' && (
          <>
            <h1 className="font-display text-3xl mb-2">Welcome to {state.team_name}</h1>
            <p className="text-ink-600 mb-6">You're in. Open the shared workspace to see what your team is working on.</p>
            <div className="flex gap-2">
              <Link to="/team/workspace" className="btn-primary">
                Open workspace <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/learn" className="text-ink-600 hover:text-ink-900 px-4 py-2">
                Go to Today
              </Link>
            </div>
          </>
        )}

        {state.status === 'overflow' && (
          <>
            <h1 className="font-display text-3xl mb-2">{state.team_name} is full</h1>
            <p className="text-ink-600 mb-6">Pick how you'd like to join. You can still be part of the shared workspace either way.</p>

            <div className="space-y-3">
              <button
                onClick={joinAtPro}
                disabled={working}
                className="w-full text-left bg-warm-50 border border-ink-100 rounded-2xl p-5 hover:border-ink-300 transition disabled:opacity-50"
              >
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-accent-700 mt-0.5" />
                  <div>
                    <div className="font-medium">Join at Pro — $10 a month</div>
                    <p className="text-sm text-ink-600 mt-1">
                      You pay personally, get full Pro features, and join the team's shared workspace right away.
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={requestSecondTeam}
                disabled={working}
                className="w-full text-left bg-warm-50 border border-ink-100 rounded-2xl p-5 hover:border-ink-300 transition disabled:opacity-50"
              >
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-accent-700 mt-0.5" />
                  <div>
                    <div className="font-medium">Ask the owner to add a second Team plan</div>
                    <p className="text-sm text-ink-600 mt-1">
                      Sends an in-app request. Once they approve, you'll be added as a team seat.
                    </p>
                  </div>
                </div>
              </button>
            </div>
          </>
        )}

        {state.status === 'error' && (
          <>
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-5 h-5 text-coral-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-ink-700">{state.message}</p>
            </div>
            <Link to="/learn" className="btn-primary inline-flex">
              Go to Today <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
